import { FileDownload } from "@mui/icons-material";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TablePagination, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { formatDate } from "../../utils";
import { useDispatch } from "react-redux";
import { getUnlistedHoldings } from "../../redux/rm/rmSlice";
import { getRMUser } from "../../redux/user/userSlice";
import * as XLSX from "xlsx";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";

const MisReport = () => {
  const dispatch = useDispatch<any>();
  const userId = Number(localStorage.getItem("ID") || "0");
  const userType = Number(localStorage.getItem("userType") || "0");
  const [transactionsData, setTransactionsData] = React.useState<any>([]);
  const [rms, setRms] = React.useState<any[]>([]);
  const [filterRMId, setFilterRMId] = React.useState<number>(0);
  const [filterClient, setFilterClient] = React.useState("");
  const [filterScrip, setFilterScrip] = React.useState("");
  const [filterType, setFilterType] = React.useState("");
  const [filterStartDate, setFilterStartDate] = React.useState("");
  const [filterEndDate, setFilterEndDate] = React.useState("");
  const [filteredData, setFilteredData] = React.useState<any[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  useEffect(() => {
    getUnlistedHoldingData();
    if (userType === 4) fetchRms();
  }, []);

  const fetchRms = async () => {
    try {
      const response = await dispatch(getRMUser({ userType: 2, pageIndex: 1, pageSize: 1000 }));
      setRms(response?.payload?.data || []);
    } catch (error) {
      console.error("Error fetching RMs:", error);
    }
  };

  const getUnlistedHoldingData = async () => {
    try {
      const res = await dispatch(
        getUnlistedHoldings({ rmId: userType === 4 ? 0 : userId })
      );
      if (res?.error?.message) {
        return;
      }

      setTransactionsData(res?.payload?.data?.transactions || []);
      setFilteredData(res?.payload?.data?.transactions || []);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const columnStyles = [
    { label: "SL NO", minWidth: 140 },
    { label: "Deal Date", minWidth: 160 },
    { label: "Branch", minWidth: 160 },
    { label: "RM1", minWidth: 180 },
    { label: "RM2", minWidth: 180 },
    { label: "Party", minWidth: 150 },
    { label: "Script", minWidth: 150 },
    { label: "Mode", minWidth: 120 },
    { label: "Broker", minWidth: 120 },
    { label: "QTY", minWidth: 120 },
    { label: "Rate", minWidth: 100 },
    { label: "Amount", minWidth: 120 },
    { label: "GP", minWidth: 160 },
    { label: "Gross Profit", minWidth: 160 },
    { label: "Incentive", minWidth: 160 },
    { label: "Incentive Amt", minWidth: 160 },
    { label: "DP Price", minWidth: 160 },
    { label: "Profit Share", minWidth: 160 },
    { label: "Remark", minWidth: 180 },
    { label: "Purchase Price", minWidth: 160 },
    { label: "GP", minWidth: 160 },
    { label: "Gross Profit", minWidth: 160 },
    { label: "Profit", minWidth: 160 },
    { label: "Sharing", minWidth: 160 },
    { label: "Net Profit", minWidth: 160 },
    { label: "% Sharing", minWidth: 160 },
  ];
  const getSerialedData = (data: any[]) => {
    let buyCount = 0;
    let sellCount = 0;

    return data.map((txn) => {
      if (txn.type === "BUY") {
        buyCount += 1;
        return { ...txn, serialNo: `P${buyCount}` };
      }
      if (txn.type === "SELL") {
        sellCount += 1;
        return { ...txn, serialNo: `S${sellCount}` };
      }
      return { ...txn, serialNo: "-" };
    });
  };
  const serialedData = getSerialedData(filteredData);
  const paginatedData = serialedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const uniqueValues = (key: string): string[] => {
    return Array.from(
      new Set(
        transactionsData
          .map((item: any) => item[key])
          .filter(
            (v: any): v is string => typeof v === "string" && v.trim() !== ""
          )
      )
    );
  };

  useEffect(() => {
    let data = [...transactionsData];
    if (filterRMId) {
      const selectedRM = rms.find((r) => r.id === filterRMId);
      if (selectedRM) {
        const matchName = selectedRM.firstName.trim().toLowerCase();
        data = data.filter((t) => 
          (t.rmName || "").toLowerCase().includes(matchName) || 
          (t.rmName2 || "").toLowerCase().includes(matchName) ||
          Number(t.rmId) === filterRMId || 
          Number(t.rmId2) === filterRMId
        );
      } else {
        data = data.filter((t) => Number(t.rmId) === filterRMId || Number(t.rmId2) === filterRMId);
      }
    }
    if (filterType) data = data.filter((t) => t.type?.toUpperCase() === filterType.toUpperCase());
    if (filterClient) data = data.filter((t) => (t.investorName || "").toLowerCase().includes(filterClient.toLowerCase()));
    if (filterScrip) data = data.filter((t) => (t.stockName || "").toLowerCase().includes(filterScrip.toLowerCase()));
    if (filterStartDate) data = data.filter((t) => new Date(t.createdAt) >= new Date(filterStartDate));
    if (filterEndDate) { const end = new Date(filterEndDate); end.setHours(23,59,59,999); data = data.filter((t) => new Date(t.createdAt) <= end); }
    setFilteredData(data);
    setPage(0);
  }, [filterRMId, filterType, filterClient, filterScrip, filterStartDate, filterEndDate, transactionsData]);
  const cellEllipsis = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const exportToCSV = () => {
    let buyCount = 0;
    let sellCount = 0;
    const tableData = serialedData.map((txn: any) => ({
      "SL NO": txn.serialNo,
      "Deal Date": formatDate(txn.createdAt),
      Branch: txn.branchName || "-",
      "RM1": txn.rmName || "-",
      "RM2": txn.rmName2 || "-",
      "Party": txn.investorName || "-",
      "Script": txn.stockName || "-",
      "Mode": txn.type || "-",
      "Broker": txn.brokerName || "-",
      "QTY": txn.quantity || "-",
      "Rate": txn.buySellPrice || "-",
      "Amount": txn.investedAmount ? `₹${txn.investedAmount}` : "-",
      "GP": txn.gp || "-",
      "Gross Profit": "-", // txn.grossprofit || "-",
      "Incentive": txn.incentive || "-",
      "Incentive Amt": txn.incentiveamnt || "-",
      "DP Price": txn.dpprice || "-",
      "Profit Share": txn.profitshare || "-",
      "Remark": txn.remarks || "-",
      "Purchase Price": txn.pp || "-",
      GP2: txn.gp2 || "-",
      "Gross Profit 2": "-", // txn.grossprofit2 || "-",
      Profit: "-", // txn.profit || "-",
      Sharing: txn.sharing || "-",
      "Net Profit": "-", // txn.netprofit || "-",
      "% Sharing": txn.percentagesharing || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction Status");

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `MIS_Report_${currentDate}.csv`;
    XLSX.writeFile(workbook, filename, { bookType: "csv" });
  };

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: "20px",
      }}
    >
      <Box
        width="98%"
        mb={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" style={{ textAlign: "left" }}>
          MIS Report
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownload />}
          onClick={exportToCSV}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Export Data
        </Button>
      </Box>
      {/* Multi-Filter Bar */}
      <Box width="98%" mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        {userType === 4 && (
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="rm-filter-label">RM</InputLabel>
            <Select
              labelId="rm-filter-label"
              value={filterRMId}
              onChange={(e) => setFilterRMId(Number(e.target.value))}
              label="RM"
            >
              <MenuItem value={0}>All RMs</MenuItem>
              {rms.map((rm: any) => (
                <MenuItem key={rm.id} value={rm.id}>{rm.firstName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="client-filter-label">Client</InputLabel>
          <Select
            labelId="client-filter-label"
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            label="Client"
          >
            <MenuItem value="">All Clients</MenuItem>
            {Array.from(new Set(transactionsData.map((t: any) => t.investorName).filter(Boolean))).sort().map((name: any) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <input
          placeholder="Filter by Script Name"
          value={filterScrip}
          onChange={(e) => setFilterScrip(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc", minWidth: 160 }}
        />
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
        />
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
        />
        <FormControl sx={{ minWidth: 130 }} size="small">
          <InputLabel id="type-filter-label">Type</InputLabel>
          <Select
            labelId="type-filter-label"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Type"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="BUY">Buy</MenuItem>
            <MenuItem value="SELL">Sell</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={() => { setFilterRMId(0); setFilterType(""); setFilterClient(""); setFilterScrip(""); setFilterStartDate(""); setFilterEndDate(""); }}>
          Clear
        </Button>
      </Box>
      <Box
        width="98%"
        mt={2}
        sx={{ overflowX: "auto", border: "1px solid #ccc" }}
      >
        <Box sx={{ width: "fit-content" }}>
          <Box
            sx={{
              display: "flex",
              bgcolor: "grey.100",
              fontWeight: 600,
              borderBottom: "2px solid #ccc",
            }}
          >
            {columnStyles.map((col, idx) => (
              <Box
                key={idx}
                sx={{
                  minWidth: col.minWidth,
                  p: 1,
                  borderRight: "none",
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
          {paginatedData.map((txn: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ minWidth: 140, p: 1 }}>{txn.serialNo}</Box>

              <Box sx={{ minWidth: 160, p: 1 }}>
                {formatDate(txn.createdAt) || "-"}
              </Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.branchName || "-"}</Box>
              <Box sx={{ minWidth: 180, p: 1 }}>{txn.rmName || "-"}</Box>
              <Box sx={{ minWidth: 180, p: 1 }}>{txn.rmName2 || "-"}</Box>
              <Box
                sx={{
                  minWidth: 150,
                  maxWidth: 150,
                  p: 1,
                  ...cellEllipsis,
                }}
                title={txn.investorName}
              >
                {txn.investorName || "-"}
              </Box>

              <Box
                sx={{
                  minWidth: 150,
                  maxWidth: 150,
                  p: 1,
                  ...cellEllipsis,
                }}
                title={txn.stockName}
              >
                {txn.stockName || "-"}
              </Box>

              <Box sx={{ minWidth: 120, p: 1 }}>{txn.type || "-"}</Box>
              <Box sx={{ minWidth: 120, p: 1 }}>{txn.brokerName || "-"}</Box>
              <Box sx={{ minWidth: 120, p: 1 }}>{txn.quantity || "-"}</Box>
              <Box sx={{ minWidth: 120, p: 1 }}>{txn.buySellPrice || "-"}</Box>
              <Box sx={{ minWidth: 120, p: 1 }}>
                ₹{txn.investedAmount || "-"}
              </Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.gp || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{"-"/* txn.grossprofit || "-" */}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.incentive || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.incentiveamnt || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.dpprice || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.profitshare || "-"}</Box>
              <Box sx={{ minWidth: 180, p: 1 }}>{txn.remarks || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.pp || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.gp2 || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{"-"/* txn.grossprofit2 || "-" */}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{"-"/* txn.profit || "-" */}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{txn.sharing || "-"}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>{"-"/* txn.netprofit || "-" */}</Box>
              <Box sx={{ minWidth: 160, p: 1 }}>
                {txn.percentagesharing || "-"}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box width="98%" display="flex" justifyContent="flex-end" mt={2}>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e: any) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </div>
  );
};

export default MisReport;
